<template>
  <div v-if="!result" class="calculation-steps">
    <h2 class="h4">
      How to Find the Sample Variance: A Step-by-Step Calculation
    </h2>
    <p class="bottom-margins">
      Typically, statistical analysis software will automatically find the variance for you. You can also obtain it with our Variance Calculator. But knowing how to calculate variance by hand can help you better understand how the formula works.
    </p>
    <p>
      To calculate the <span class="bolded-texts">sample variance</span> of a dataset, follow these steps:
    </p>
    <ol>
      <li>
        <p class="top-margins">
          Calculate the sample mean of the dataset:
        </p>
        <p>
          \[\bar{X}=\frac{\sum_{i=1}^{n}X_i}{n}\]
        </p>
      </li>
      <li>
        <p>Subtract the mean from each value in the dataset:</p>
        <p>
          \[(X_i-\bar{X})\]
        </p>
      </li>
      <li>
        <p>Calculate each observation’s squared deviation from the sample mean:</p>
        <p>
          \[{(X_i-\bar{X})}^2\]
        </p>
      </li>
      <li>
        <p>Calculate the sum of all the squared differences to obtain the sum of squares (SS):</p>
        <p>
          \[\sum_{i=1}^{n}{(X_i-\bar{X})}^2\]
        </p>
      </li>
      <li>
        <p>Divide the sum of squared differences by n – 1 to obtain the variance (<span class="inline-formulas">\[s^2\]</span>):</p>
        <p>
          \[s^2=\sum_{i=1}^{n}\frac{(X_i-\bar{X})^2}{n-1}\]
        </p>
      </li>
    </ol>

    <h2 class="h4 additional-h2">
      How to Find the Population Variance: A Step-by-Step Calculation
    </h2>
    <p class="bottom-margins">
      To calculate the <span class="bolded-texts">population variance</span>, follow these steps:
    </p>
    <ol>
      <li>
        <p>
          Calculate the population mean of the dataset:
        </p>
        <p>
          \[\mu=\frac{\sum_{i=1}^{n}X_i}{n}\]
        </p>
      </li>
      <li>
        <p>Subtract the mean from each value in the dataset:</p>
        <p>
          \[(X_i-\mu)\]
        </p>
      </li>
      <li>
        <p>Calculate each observation’s squared deviation from the population mean:</p>
        <p>
          \[{(X_i-\mu)}^2\]
        </p>
      </li>
      <li>
        <p>Calculate the sum of all the squared differences to obtain the sum of squares (SS):</p>
        <p>
          \[\sum_{i=1}^{n}{(X_i-\mu)}^2\]
        </p>
      </li>
      <li>
        <p>Divide the sum of squared differences by n to obtain the population variance (<span class="inline-formulas">\[\sigma^2\]</span>):</p>
        <p>
          \[\sigma^2=\sum_{i=1}^{n}\frac{(X_i-\mu)^2}{n}\]
        </p>
      </li>
      <li>
        <p>Take the square root of the sum of the squared deviations to calculate the population standard deviation:</p>
        <p class="default-last-formula">
          \[\sigma=\sqrt{\sum_{i=1}^{n}\frac{(X_i-\mu)^2}{n}}\]
        </p>
      </li>
    </ol>
  </div>

  <div v-else :key="result" class="calculation-steps result">
    <h2 class="h4">
      Calculation Steps:
    </h2>
    <p class="bottom-margins">
      Follow these steps to obtain the {{ calculationData.method }} variance of your dataset:
    </p>
    <ol>
      <li>
        <p>Calculate the {{ calculationData.method }} mean of the dataset:</p>
        <p v-if="calculationData.method === 'sample'" class="formulas">
          \[\bar{X}=\frac{\sum_{i=1}^{n}X_i}{n}=\frac{ {{ additionString }} }{ {{ calculationData.size }} }={{ calculationData.mean }}\]
        </p>
        <p v-else class="formulas">
          \[\mu=\frac{\sum_{i=1}^{n}X_i}{n}=\frac{ {{ additionString }} }{ {{ calculationData.size }} }={{ calculationData.mean }}\]
        </p>
      </li>
      <li>
        <p class="top-margins">
          Subtract the mean from each value in the dataset. We have the following equation for the first variable:
        </p>
        <p v-if="calculationData.method === 'sample'" class="formulas">
          \[(X_i-\bar{X})={{ calculationData.rawDataMeanArray[0] }}-{{ calculationData.mean }}={{ calculationData.deviationsFromMeanFirstValue }}\]
        </p>
        <p v-else class="formulas">
          \[(X_i-\mu)={{ calculationData.rawDataMeanArray[0] }}-{{ calculationData.mean }}={{ calculationData.deviationsFromMeanFirstValue }}\]
        </p>
      </li>
      <li>
        <p class="top-margins">
          Calculate each observation’s squared deviation from the {{ calculationData.method }} mean. We have the following equation for the first variable:
        </p>
        <p v-if="calculationData.method === 'sample'" class="formulas">
          \[{(X_i-\bar{X})}^2={ {{ calculationData.deviationsFromMeanFirstValue }} }^2={{ calculationData.deviationsFromMeanSquaredFirstValue }}\]
        </p>
        <p v-else class="formulas">
          \[{(X_i-\mu)}^2={ {{ calculationData.deviationsFromMeanFirstValue }} }^2={{ calculationData.deviationsFromMeanSquaredFirstValue }}\]
        </p>
      </li>
      <li>
        <p class="top-margins">
          Calculate the sum of all the squared differences to obtain the sum of squares (SS):
        </p>
        <p v-if="calculationData.method === 'sample'" class="formulas">
          \[\sum_{i=1}^{n}{\left(X_i-\bar{X}\right)^2=\sum_{i=1}^{n}{ {{ sqrString }} }={{ calculationData.sumOfSquaredDeviations }}}\]
        </p>
        <p v-else class="formulas">
          \[\sum_{i=1}^{n}{\left(X_i-\mu\right)^2=\sum_{i=1}^{n}{ {{ sqrString }} }={{ calculationData.sumOfSquaredDeviations }}}\]
        </p>
      </li>
      <li>
        <div v-if="calculationData.method === 'sample'" class="top-margins">
          <p>Divide the sum of squared differences by n – 1 to obtain the variance (<span class="inline-formulas">\[s^2\]</span>).</p>
          <p class="formulas">
            \[s^2=\sum_{i=1}^{n}\frac{(X_i-\bar{X})^2}{n-1}=\frac{ {{ calculationData.sumOfSquaredDeviations }} }{ {{ calculationData.size }}-1}={{ calculationData.variance }}\]
          </p>
        </div>
        <div v-else class="top-margins">
          <p>Divide the sum of squared differences by n to obtain the population variance (<span class="inline-formulas">\[\sigma^2\]</span>).</p>
          <p class="formulas">
            \[\sigma^2=\sum_{i=1}^{n}\frac{(X_i-\mu)^2}{n}=\frac{ {{ calculationData.sumOfSquaredDeviations }} }{ {{ calculationData.size }} }={{ calculationData.variance }}\]
          </p>
        </div>
      </li>
    </ol>
    <p class="small-margins">
      The {{ calculationData.method }} variance is equal to <span class="bolded-texts">{{ calculationData.variance }}</span>
    </p>

    <div v-if="calculationData.method === 'sample'">
      <div class="python-r-codes python">
        <span class="img-text"><img alt="Python icon" src="../../../../assets/images/icon-python-calc.png"> <h4 class="h6">Your Result in Python:</h4></span>
        <p class="codes-info">
          The following Python code should provide the variance using the input data you entered.
        </p>
        <div class="codes-container">
          <div class="codes">
            <pre
              class="language-python"
            ><code id="python-sample"># Import relevant library

import statistics

data = <span class="no-wrap">{{ inputToNumbers }}</span>

# Calculate the sample variance

variance = statistics.variance(data)

print("Sample variance:", variance)</code></pre>
          </div>
        </div>
        <button class="btn-link-accent" :class="{ copied: copyCodeOrCopiedPython === 'Copied' }" @click="copyCode('python-sample')">
          {{ copyCodeOrCopiedPython }}
        </button>
      </div>
      <div class="python-r-codes">
        <span class="img-text"><img alt="Icon for R language" src="../../../../assets/images/icon-r-calc.png"> <h4 class="h6">Your Result in R:</h4></span>
        <p class="codes-info">
          The following R code should provide the variance using the input data you entered.
        </p>
        <div class="codes-container">
          <div class="codes">
            <pre
              class="language-r"
            ><code id="r-sample"># Create vector of data

data &lt;- c(<span class="no-wrap">{{ arrayToString }}</span>)

# Calculate the sample variance

sample_variance &lt;- var(data)

cat("Sample variance:", sample_variance, "\n")</code></pre>
          </div>
        </div>
        <button class="btn-link-accent" :class="{ copied: copyCodeOrCopiedR === 'Copied' }" @click="copyCode('r-sample')">
          {{ copyCodeOrCopiedR }}
        </button>
      </div>
    </div>
    <div v-else>
      <div class="python-r-codes python">
        <span class="img-text"><img alt="Python icon" src="../../../../assets/images/icon-python-calc.png"> <h4 class="h6">Your Result in Python:</h4></span>
        <p class="codes-info">
          The following Python code should provide the variance using the input data you entered.
        </p>
        <div class="codes-container">
          <div class="codes">
            <pre
              class="language-python"
            ><code id="python-population"># Import relevant library

import statistics

data = <span class="no-wrap">{{ inputToNumbers }}</span>

# Calculate the population variance

variance = statistics.pvariance(data)

print("Population variance:", variance)</code></pre>
          </div>
        </div>
        <button class="btn-link-accent" :class="{ copied: copyCodeOrCopiedPython === 'Copied' }" @click="copyCode('python-population')">
          {{ copyCodeOrCopiedPython }}
        </button>
      </div>
      <div class="python-r-codes">
        <span class="img-text"><img alt="Icon for R language" src="../../../../assets/images/icon-r-calc.png"> <h4 class="h6">Your Result in R:</h4></span>
        <p class="codes-info">
          The following R code should provide the variance using the input data you entered.
        </p>
        <div class="codes-container">
          <div class="codes">
            <pre
              class="language-r"
            ><code id="r-population"># Create vector of data

data &lt;- c(<span class="no-wrap">{{ arrayToString }}</span>)

n &lt;- length(data)

# Calculate the population variance

population_variance &lt;- var(data)*(n-1)/n

cat("Sample variance:", population_variance, "\n")</code></pre>
          </div>
        </div>
        <button class="btn-link-accent" :class="{ copied: copyCodeOrCopiedR === 'Copied' }" @click="copyCode('r-population')">
          {{ copyCodeOrCopiedR }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';

export default {
  data() {
    return {
      copyCodeOrCopiedPython: 'Copy Code',
      copyCodeOrCopiedR: 'Copy Code',
    };
  },
  computed: {
    ...mapState('calculators', ['result', 'calculationData']),
    additionString() {
      const { length } = this.calculationData.rawDataMeanArray;
      const rawDataMeanArray = this.calculationData.rawDataMeanArray.map((num, i) => (length - 1 > i ? `${num} + ` : num));
      return rawDataMeanArray.join('');
    },
    sqrString() {
      const { length } = this.calculationData.rawDataMeanArray;
      const rawDataMeanArray = this.calculationData.rawDataMeanArray.map((num, i) => (length - 1 > i ? `(${num} - ${this.calculationData.mean})^2 + ` : `(${num} - ${this.calculationData.mean})^2`));
      return rawDataMeanArray.join('');
    },
    inputToNumbers() {
      return this.calculationData.rawDataMeanArray.map((str) => Number(str));
    },
    arrayToString() {
      return this.calculationData.rawDataMeanArray.join(', ');
    },
  },
  methods: {
    copyCode(id) {
      if (id.split('-')[0] === 'python') {
        this.copyCodeOrCopiedPython = 'Copied';
        setTimeout(() => {
          this.copyCodeOrCopiedPython = 'Copy Code';
        }, 1000);
      } else {
        this.copyCodeOrCopiedR = 'Copied';
        setTimeout(() => {
          this.copyCodeOrCopiedR = 'Copy Code';
        }, 1000);
      }

      const currentText = document.getElementById(id).innerText;
      this.$copy(currentText);
    },
  },
};
</script>

<style lang="scss" scoped>
.calculation-steps {
  p {
    margin: 0;
  }

  .python-r-codes.python {
    margin-top: var(--spacing-80);
  }

  ul {
    padding-left: 0;
  }

  .additional-h2 {
    margin-top: var(--spacing-24);
  }

  .math-jax-bottom-margins {
    margin-bottom: var(--spacing-16);
  }

  .math-jax-top-bottom-margins {
    margin: var(--spacing-16) 0;
  }
}

@include bp($bp-mobile) {
  div .calculation-steps {
    padding-top: var(--spacing-40);

    .h4 {
      text-align: left;
    }
  }
}

@include bp($bp-phone) {
  .calculation-steps {
    .additional-h2 {
      margin-top: 64px;
    }
  }
}

@import '../../../../assets/sass/ds/calculators/calculation-steps.scss';
</style>
